/**
 * Burgid - Upsell add-ons (pattern chart + handwritten note card)
 *
 * Horizon theme block. When at least one add-on is ticked it takes over the product
 * form submit and posts the doll plus its add-ons as one atomic /cart/add.js call,
 * then dispatches the theme's own CartAddEvent so the cart drawer opens and refreshes.
 *
 * With nothing ticked it does not intervene at all - ProductFormComponent.handleSubmit
 * runs exactly as it normally would.
 */
import { CartAddEvent, ThemeEvents } from '@theme/events';

/**
 * Format cents using the shop's money_format string, so the block matches the rest
 * of the storefront (including comma-separator and no-decimal currencies).
 * @param {number} cents
 * @param {string} format
 */
function formatMoney(cents, format) {
  const amount = Number(cents) || 0;
  const match = /\{\{\s*(\w+)\s*\}\}/.exec(format || '');
  if (!match) return (amount / 100).toFixed(2);

  const withDecimals = !match[1].includes('no_decimals');
  const useComma = match[1].includes('comma_separator');

  let value = (amount / 100).toFixed(withDecimals ? 2 : 0);
  let [whole, fraction] = value.split('.');
  whole = whole.replace(/\B(?=(\d{3})+(?!\d))/g, useComma ? '.' : ',');
  value = fraction ? whole + (useComma ? ',' : '.') + fraction : whole;

  return format.replace(match[0], value);
}

class BurgidUpsellAddons extends HTMLElement {
  #form = null;
  #config = null;
  #busy = false;
  #ready = false;
  #noteError = false;
  /** @type {AbortController | null} */
  #abort = null;

  #initAttempts = 0;

  connectedCallback() {
    if (this.#ready) return;

    const configEl = this.querySelector('[data-ua-config]');
    if (!configEl) {
      // The theme editor can attach the element before its children are in place.
      if (this.#initAttempts++ < 5) setTimeout(() => this.connectedCallback(), 0);
      return;
    }

    try {
      this.#config = JSON.parse(configEl.textContent);
    } catch (error) {
      return;
    }

    const form = document.getElementById(this.#config.formId);
    if (!(form instanceof HTMLFormElement)) return;

    this.#form = form;
    this.#ready = true;
    this.#abort = new AbortController();

    this.#wire();
    this.#render();
  }

  disconnectedCallback() {
    this.#abort?.abort();
    this.#restoreAtcLabel();
    this.#form?.querySelector('.shopify-payment-button')?.classList.remove('ua-hide-buy-now');
    // Allow a full re-init if the theme editor moves the block back into the page.
    this.#ready = false;
    this.#initAttempts = 0;
  }

  /* ---- element lookups ---------------------------------------------------- */

  get #el() {
    return {
      pattern: this.querySelector('[data-ua-toggle="pattern"]'),
      gift: this.querySelector('[data-ua-toggle="gift"]'),
      patternRow: this.querySelector('[data-ua-row="pattern"]'),
      giftRow: this.querySelector('[data-ua-row="gift"]'),
      noteWrap: this.querySelector('[data-ua-note-wrap]'),
      note: this.querySelector('[data-ua-note]'),
      counters: this.querySelectorAll('[data-ua-counter]'),
      count: this.querySelector('[data-ua-count]'),
      total: this.querySelector('[data-ua-total]'),
      save: this.querySelector('[data-ua-save]'),
      error: this.querySelector('[data-ua-error]'),
    };
  }

  /** The theme morphs this button on variant change, so it is re-queried every time. */
  get #atcTextNode() {
    return this.#form?.querySelector(
      'add-to-cart-component [ref="addToCartButton"] .add-to-cart-text__content span span'
    );
  }

  get #atcButton() {
    return this.#form?.querySelector('add-to-cart-component [ref="addToCartButton"]');
  }

  /* ---- reading the theme's form ------------------------------------------- */

  #variantId() {
    const input = this.#form?.querySelector('input[name="id"]');
    return input?.value ? String(input.value) : '';
  }

  #quantity() {
    const selector = this.#form?.querySelector('quantity-selector-component');
    const fromComponent = Number(selector?.getValue?.());
    if (fromComponent > 0) return fromComponent;

    const input = this.#form?.querySelector('[name="quantity"]');
    const value = Number(input?.value);
    return value > 0 ? value : 1;
  }

  #basePrice() {
    const id = this.#variantId();
    const variants = this.#config.variants || [];
    const match = variants.find((variant) => String(variant.id) === id);
    return match ? match.price : variants[0]?.price || 0;
  }

  #money(cents) {
    return formatMoney(cents, this.#config.moneyFormat);
  }

  /* ---- render -------------------------------------------------------------- */

  #render = () => {
    if (!this.#ready) return;

    const el = this.#el;
    const { pattern, gift } = this.#config;
    const qty = this.#quantity();

    const patternOn = Boolean(pattern && el.pattern?.checked);
    const giftOn = Boolean(gift && el.gift?.checked);

    el.patternRow?.classList.toggle('is-on', patternOn);
    el.giftRow?.classList.toggle('is-on', giftOn);
    if (el.noteWrap) el.noteWrap.hidden = !giftOn;

    const total =
      this.#basePrice() * qty + (patternOn ? pattern.price : 0) + (giftOn ? gift.price * qty : 0);

    const lines = 1 + (patternOn ? 1 : 0) + (giftOn ? 1 : 0);
    if (el.count) {
      el.count.textContent =
        lines === 1 ? this.#config.strings.oneItem : this.#config.strings.manyItems.replace('[n]', lines);
    }
    if (el.total) el.total.textContent = this.#money(total);

    const saving = patternOn && pattern.compareAt > pattern.price ? pattern.compareAt - pattern.price : 0;
    if (el.save) {
      el.save.hidden = saving <= 0;
      if (saving > 0) el.save.textContent = this.#config.strings.save.replace('[amount]', this.#money(saving));
    }

    if (el.note) {
      const used = el.note.value.length;
      const max = this.#config.maxNote;
      el.counters.forEach((counter) => {
        counter.textContent = `${used}/${max}`;
        counter.classList.toggle('is-warn', used > max * 0.9);
      });
    }

    // Drop the prompt as soon as it stops applying - but leave a cart error from Shopify up.
    if (this.#noteError && !this.#missingNote()) this.#clearNoteError();

    this.#toggleBuyNow(patternOn || giftOn);
    this.#updateAtcLabel(total);
  };

  #updateAtcLabel(total) {
    if (!this.#config.updateAtcLabel) return;

    const node = this.#atcTextNode;
    const button = this.#atcButton;
    if (!node || !button) return;

    if (!node.dataset.uaBase) node.dataset.uaBase = (node.textContent || '').trim();

    // Sold out / unavailable - leave the theme's own wording alone.
    node.textContent = button.disabled
      ? node.dataset.uaBase
      : `${node.dataset.uaBase} \u2014 ${this.#money(total)}`;
  }

  #restoreAtcLabel() {
    const node = this.#atcTextNode;
    if (node?.dataset.uaBase) node.textContent = node.dataset.uaBase;
  }

  #showError(message) {
    const { error } = this.#el;
    if (!error) return;
    error.textContent = message || '';
    error.hidden = !message;
  }

  /**
   * A note card with nothing written on it reaches the buyer blank and reaches Burgid as a
   * "why was I charged $4.95" email, so it is never allowed into the cart.
   */
  #missingNote() {
    const el = this.#el;
    if (!this.#config.gift || !el.gift?.checked) return false;
    return !el.note || el.note.value.trim() === '';
  }

  #showNoteError() {
    const { note, giftRow } = this.#el;
    this.#noteError = true;
    this.#showError(this.#config.strings.noteRequired);
    note?.classList.add('is-invalid');
    note?.setAttribute('aria-invalid', 'true');

    // Focus without scrolling, then place the card ourselves: focus() alone lands on the
    // field's top edge, which on mobile often sits under the sticky header.
    note?.focus({ preventScroll: true });
    this.#revealNote();

    if (giftRow) {
      // Restart the animation when the shopper clicks Add to cart a second time.
      giftRow.classList.remove('is-shaking');
      void giftRow.offsetWidth;
      giftRow.classList.add('is-shaking');

      // Reduced motion turns the animation off entirely, so animationend never fires -
      // the timeout is what actually clears the class for those shoppers.
      const stop = () => giftRow.classList.remove('is-shaking');
      giftRow.addEventListener('animationend', stop, { once: true });
      setTimeout(stop, 600);
    }
  }

  /** Bring the note card into view when the shopper cannot see what is blocking them. */
  #revealNote() {
    const row = this.#el.giftRow;
    if (!row) return;

    const rect = row.getBoundingClientRect();
    if (rect.top >= 0 && rect.bottom <= window.innerHeight) return;

    const reduced = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    row.scrollIntoView({ behavior: reduced ? 'auto' : 'smooth', block: 'center' });
  }

  #clearNoteError() {
    const { note, giftRow } = this.#el;
    this.#noteError = false;
    this.#showError('');
    note?.classList.remove('is-invalid');
    note?.removeAttribute('aria-invalid');
    giftRow?.classList.remove('is-shaking');
  }

  /**
   * Shop Pay's Buy it now runs its own checkout straight from the form and never sees the
   * add-ons, so a shopper who ticks the pattern and then buys with it pays for the doll
   * alone. Hide it while anything is ticked rather than let that happen quietly.
   */
  #toggleBuyNow(anyAddon) {
    if (!this.#config.hideBuyNow) return;

    const wrap = this.#form?.querySelector('.shopify-payment-button');
    wrap?.classList.toggle('ua-hide-buy-now', anyAddon);
  }

  /* ---- cart ---------------------------------------------------------------- */

  #addonItems() {
    const el = this.#el;
    const { pattern, gift } = this.#config;
    const qty = this.#quantity();
    const items = [];

    if (pattern && el.pattern?.checked) {
      // A PDF is bought once, however many dolls are in the order.
      items.push({ id: pattern.id, quantity: 1 });
    }

    if (gift && el.gift?.checked) {
      const item = { id: gift.id, quantity: qty };
      const message = el.note?.value.trim();
      if (message) item.properties = { [this.#config.notePropertyName]: message };
      items.push(item);
    }

    return items;
  }

  /**
   * Burgid's cart drawer is the Essential Cart Drawer app, not Horizon's own. It opens by
   * watching the theme's submit flow, which this block deliberately bypasses, so it has to
   * be told directly - otherwise the shopper gets a silent add with only the header count
   * moving. CartAddEvent above already covers the native drawer, and this is a no-op on any
   * store without the app.
   */
  #openAppCartDrawer() {
    const api = window.essentialCartApi;
    if (!api || typeof api.openCart !== 'function') return;

    try {
      api.refreshCart?.();
      api.openCart();
    } catch (error) {
      // The drawer is a nicety; never let it break the add itself.
    }
  }

  /** Section ids the theme needs re-rendered, matching ProductFormComponent. */
  #cartSectionIds() {
    return Array.from(document.querySelectorAll('cart-items-component'))
      .map((node) => node.dataset.sectionId)
      .filter(Boolean)
      .join(',');
  }

  async #submitAll(addons) {
    const id = this.#variantId();
    if (!id) return;

    this.#busy = true;
    this.classList.add('is-busy');
    this.#showError('');

    const quantity = this.#quantity();
    const items = [{ id: Number(id), quantity }, ...addons];
    const sections = this.#cartSectionIds();

    try {
      const response = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(sections ? { items, sections } : { items }),
      });
      const data = await response.json();

      // /cart/add.js is atomic - if one line fails, nothing is added.
      if (data.status) {
        this.#showError(data.description || data.message || this.#config.strings.genericError);
        return;
      }

      const host = this.#form.closest('product-form-component') || this;
      host.dispatchEvent(
        new CartAddEvent({}, id, {
          source: 'product-form-component',
          itemCount: items.reduce((sum, item) => sum + item.quantity, 0),
          productId: this.#config.productId,
          sections: data.sections,
        })
      );

      this.#openAppCartDrawer();
    } catch (error) {
      window.location.href = this.#config.cartUrl;
    } finally {
      this.#busy = false;
      this.classList.remove('is-busy');
    }
  }

  /* ---- wiring -------------------------------------------------------------- */

  #wire() {
    const { signal } = this.#abort;
    const el = this.#el;

    for (const box of [el.pattern, el.gift]) {
      box?.addEventListener(
        'change',
        () => {
          this.#render();
          if (box === el.gift && box.checked) el.note?.focus();
        },
        { signal }
      );
    }

    el.note?.addEventListener(
      'input',
      () => {
        const max = this.#config.maxNote;
        if (el.note.value.length > max) el.note.value = el.note.value.slice(0, max);
        this.#render();
      },
      { signal }
    );

    this.querySelectorAll('[data-ua-preset]').forEach((chip) => {
      chip.addEventListener(
        'click',
        () => {
          if (!el.note) return;
          el.note.value = (chip.getAttribute('data-ua-preset') || '').slice(0, this.#config.maxNote);
          el.note.focus();
          this.#render();
        },
        { signal }
      );
    });

    // Quantity selector lives inside the form and bubbles change/input.
    this.#form.addEventListener('change', this.#render, { signal });
    this.#form.addEventListener('input', this.#render, { signal });

    // The theme morphs the add-to-cart button during a variant update, which wipes the
    // rewritten label. Render straight away for the price, then again on the next task
    // to re-apply the label once the morph has settled. Deliberately not
    // requestAnimationFrame: that never fires in a background tab, which would leave a
    // stale total on screen when the shopper comes back.
    document.addEventListener(
      ThemeEvents.variantUpdate,
      () => {
        this.#render();
        setTimeout(this.#render, 0);
      },
      { signal }
    );

    // Take over at `click`, not at `submit`.
    //
    // Horizon routes `on:submit="/handleSubmit"` through a single document-level capture
    // listener registered in component.js. That file loads before this block's module, and
    // two capture listeners on the same node fire in registration order - so intercepting
    // `submit` loses the race and ProductFormComponent adds the doll on its own before
    // stopImmediatePropagation() can stop it. Cancelling the click means the form never
    // submits at all, which is the only point this block is guaranteed to reach first.
    document.addEventListener(
      'click',
      (event) => {
        const target = event.target instanceof Element ? event.target : null;
        const button = target?.closest('[ref="addToCartButton"]');
        if (!button || !this.#form.contains(button)) return;

        this.#takeOver(event);
      },
      { capture: true, signal }
    );

    // Enter inside a single-line field (the quantity input) submits the form without any
    // click. The submit listener below loses the same registration-order race the click
    // interception was written to avoid, so the theme would add the doll on its own -
    // keydown is the only point that still comes before the submit event exists.
    // A textarea is exempt: there Enter is a newline, not a submit.
    document.addEventListener(
      'keydown',
      (event) => {
        if (event.key !== 'Enter' || event.shiftKey || event.isComposing) return;

        const target = event.target instanceof Element ? event.target : null;
        if (!target || !this.#form.contains(target)) return;
        if (target instanceof HTMLTextAreaElement) return;

        this.#takeOver(event);
      },
      { capture: true, signal }
    );

    // Last resort for any submit this block did not see coming.
    document.addEventListener(
      'submit',
      (event) => {
        if (event.target !== this.#form) return;
        this.#takeOver(event);
      },
      { capture: true, signal }
    );
  }

  /** Shared by the click and submit paths: decide whether to intervene, then do it. */
  #takeOver(event) {
    const addons = this.#addonItems();
    if (!addons.length) return; // nothing extra - let the theme handle it

    // preventDefault alone stops the form submitting, which is all it takes to keep
    // ProductFormComponent out. Propagation is left alone on the click so the store's
    // cart drawer app still sees it and opens - that drawer is an app, not the theme's
    // native one, and swallowing the click leaves the shopper with no drawer at all.
    // The submit fallback does stop propagation, because there the theme is next in line.
    event.preventDefault();
    if (event.type === 'submit') event.stopImmediatePropagation();
    if (this.#busy) return;

    if (this.#missingNote()) {
      // Nothing should react to a click that adds nothing - not even the drawer.
      event.stopImmediatePropagation();
      this.#showNoteError();
      return;
    }

    this.#submitAll(addons);
  }
}

if (!customElements.get('burgid-upsell-addons')) {
  customElements.define('burgid-upsell-addons', BurgidUpsellAddons);
}